import React, { useCallback, useEffect, useRef, useState } from 'react';
import InsightItem from './InsightItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faWandMagicSparkles, faClipboardList, faXmark, faSpinner, faCheckDouble, faTrashCan, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import ItemWrapper from './ItemWrapper';
import Modal from './Modal';
import InsightModal from './InsightModal';
import RecommendationModal from './RecommendationModal';
import SuggestionsPanel from './SuggestionsPanel';
import MergeDialog from './MergeDialog';
import BatchDedupReviewPanel from './BatchDedupReviewPanel';
import BulkReviewPanel, { ReviewItem } from './BulkReviewPanel';
import { useItemSelection } from '../hooks/useItemSelection';
import { API_URL } from '../config';
import { createNewVersion, propagateImpact, clearStatus, getDirectChildren, isActionableStatus } from '../lib';
import { findDuplicates } from '../dedup';
import { checkImpact } from '../impact';
import { useMergeDialog } from '../hooks/useMergeDialog';
import { useBatchDedupQueue } from '../hooks/useBatchDedupQueue';
import { ChatToolAction } from './ChatWidget';

type Props = {
  insightRefs: React.MutableRefObject<(HTMLDivElement | null)[]>
  data: DiscoveryData;
  setData: React.Dispatch<React.SetStateAction<DiscoveryData | null>>;
  handleMouseEnter: (entityType: string, entityId: string, data: DiscoveryData) => void;
  handleMouseLeave: (entityType: string, entityId: string, data: DiscoveryData) => void;
  onError: (msg: string) => void;
  onInfo: (msg: string) => void;
  onWaiting: (msg: string) => void;
  backendAvailable: boolean;
  onViewTraceability: (entityType: string, entityId: string) => void;
  chatActions?: ChatToolAction[];
  clearChatActions?: (filter: (a: ChatToolAction) => boolean) => void;
  requestConfirm?: (message: string, onConfirm: () => void) => void;
};

type RecommendationSuggestionData = {
  suggestions: { text: string }[];
  insightIds: string[];
};

const InsightList: React.FC<Props> = ({ insightRefs, data, setData, handleMouseEnter, handleMouseLeave, onError, onInfo, onWaiting, backendAvailable, onViewTraceability, chatActions, clearChatActions, requestConfirm }) => {

  const dataRef = useRef(data);
  dataRef.current = data;

  const [isInsightDialogVisible, setIsInsightDialogVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingInsight, setEditingInsight] = useState<ItemType | null>(null);
  const setInsightRef = useCallback((element: HTMLDivElement, index: number) => { insightRefs.current[index] = element; }, [insightRefs]);

  // Insight selection state
  const { selectedIds: selectedInsightIds, toggleSelection: toggleInsightSelection, clearSelection, selectAll } = useItemSelection(data.insights.map(n => n.insight_id));
  const [extractingRecommendations, setExtractingRecommendations] = useState(false);

  // Clear selection when discovery changes
  useEffect(() => { clearSelection(); }, [data.title, clearSelection]);
  const [recommendationSuggestionData, setRecommendationSuggestionData] = useState<RecommendationSuggestionData | null>(null);

  const [isRecommendationModalVisible, setIsRecommendationModalVisible] = useState(false);

  // Dedup merge dialog state
  const mergeDialog = useMergeDialog<InsightType>();

  // Batch dedup queue for accepted recommendation suggestions
  const recommendationDedupQueue = useBatchDedupQueue<RecommendationType>();

  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkReviewItems, setBulkReviewItems] = useState<ReviewItem[] | null>(null);

  const reviewableCount = data.insights.filter(i => isActionableStatus(i.status)).length;

  const openAddModal = () => {
    setModalMode('add');
    setEditingInsight(null);
    setIsInsightDialogVisible(true);
  };

  const openEditModal = (item: ItemType) => {
    setModalMode('edit');
    setEditingInsight(item);
    setIsInsightDialogVisible(true);
  };

  // Handle chat tool actions targeting insights
  useEffect(() => {
    if (!chatActions?.length || !clearChatActions) return;
    const matching = chatActions.filter(a => a.params.entity_type === 'insight');
    if (!matching.length) return;

    clearChatActions(a => a.params.entity_type === 'insight');

    // Batch deletes into one confirmation
    const deletes = matching.filter(a => a.tool === 'delete_item');
    if (deletes.length > 0 && requestConfirm) {
      const ids = deletes.flatMap(a =>
        Array.isArray(a.params.item_ids) ? (a.params.item_ids as string[]) : a.params.item_id ? [String(a.params.item_id)] : []
      );
      const items = data.insights.filter(n => ids.includes(n.insight_id));
      if (items.length > 0) {
        const label = items.length === 1
          ? `Delete insight "${items[0].text.substring(0, 50)}"?`
          : `Delete ${items.length} insights?`;
        const idsToDelete = new Set(items.map(i => i.insight_id));
        requestConfirm(label, () => {
          setData(prev => prev ? { ...prev, insights: prev.insights.filter(n => !idsToDelete.has(n.insight_id)) } : prev);
        });
      }
    }

    // Handle first add action
    const add = matching.find(a => a.tool === 'add_item');
    if (add) {
      setModalMode('add');
      setEditingInsight({
        insight_id: '',
        text: String(add.params.text || ''),
        related_facts: Array.isArray(add.params.related_ids) ? add.params.related_ids as string[] : [],
      } as unknown as ItemType);
      setIsInsightDialogVisible(true);
    }

    if (!add) {
      const edit = matching.find(a => a.tool === 'edit_item');
      if (edit) {
        const insight = data.insights.find(n => n.insight_id === edit.params.item_id);
        if (insight) {
          setModalMode('edit');
          setEditingInsight({
            ...insight,
            text: String(edit.params.new_text || insight.text),
          } as unknown as ItemType);
          setIsInsightDialogVisible(true);
        }
      }
    }
  }, [chatActions]); // eslint-disable-line react-hooks/exhaustive-deps

  const addInsightToData = useCallback(async (newInsight: InsightType) => {
    setData((prevState) => prevState ? ({
      ...prevState,
      insights: [...prevState.insights, newInsight]
    }) : prevState);

    // Check impact on existing recommendations
    if (data.recommendations.length > 0 && backendAvailable) {
      onWaiting('Checking impact on existing recommendations…');
      const candidates = data.recommendations.map(r => ({ id: r.recommendation_id, text: r.text }));
      const { ids: impactedIds, usedFallback } = await checkImpact('', newInsight.text, candidates, backendAvailable);
      if (impactedIds.length > 0) {
        setData(prev => prev ? ({
          ...prev,
          recommendations: prev.recommendations.map(r =>
            impactedIds.includes(r.recommendation_id)
              ? { ...r, status: 'needs_review' as const, related_insights: Array.from(new Set([...r.related_insights, newInsight.insight_id])) }
              : r
          ),
        }) : prev);
        const fallbackHint = usedFallback ? ' (AI unavailable — all recommendations marked)' : '';
        onInfo(`Insight added. ${impactedIds.length} recommendation(s) marked for review and linked.${fallbackHint}`);
      } else {
        onInfo('Insight added. No existing recommendations impacted.');
      }
    }
  }, [setData, data.recommendations, onWaiting, onInfo, backendAvailable]);

  const saveInsight = async (insightData: InsightType) => {
    if (modalMode === 'add') {
      const newInsight: InsightType = {
        insight_id: Math.random().toString(16).slice(2),
        text: insightData.text,
        related_facts: insightData.related_facts,
        version: 1,
        status: 'draft',
        created_at: new Date().toISOString(),
      };

      // Dedup guard: check for duplicates before adding
      onWaiting('Checking for duplicates…');
      const duplicates = await findDuplicates(
        insightData.text,
        data.insights.map(i => ({ id: i.insight_id, text: i.text })),
        backendAvailable,
      );

      if (duplicates.length > 0) {
        mergeDialog.show(newInsight, duplicates[0]);
        setIsInsightDialogVisible(false);
        return;
      }

      addInsightToData(newInsight);
    } else if (modalMode === 'edit' && insightData.insight_id) {
      const existing = data.insights.find(i => i.insight_id === insightData.insight_id);
      const textChanged = existing && existing.text !== insightData.text;

      if (textChanged && existing) {
          onWaiting('Creating new version and analyzing impact on related recommendations...');
          const versioned = createNewVersion(existing, insightData.text) as InsightType;
          const updatedWithRelations: InsightType = {
            ...versioned,
            related_facts: insightData.related_facts,
          };
          const intermediateData = {
            ...data,
            insights: data.insights.map(i =>
              i.insight_id === insightData.insight_id ? updatedWithRelations : i
            ),
          };
          const children = getDirectChildren('insight', insightData.insight_id, intermediateData);
          const { ids: impactedIds, usedFallback } = await checkImpact(existing.text, insightData.text, children, backendAvailable);
          const { data: propagatedData, impactedCount } = propagateImpact(
            intermediateData,
            'insight',
            insightData.insight_id,
            'edited',
            impactedIds,
          );
          setData(propagatedData);
          const fallbackHint = usedFallback ? ' (AI unavailable — all children marked)' : '';
          onInfo(`Updated to v${updatedWithRelations.version}. ${impactedCount} downstream item(s) marked for review.${fallbackHint}`);
          setIsInsightDialogVisible(false);
          return;
      }

      // No text change — update fields and check if links or weight changed
      const linksChanged = existing &&
        JSON.stringify([...existing.related_facts].sort()) !== JSON.stringify([...insightData.related_facts].sort());
      const weightChanged = existing && existing.weight !== insightData.weight;

      const updatedInsights = data.insights.map((insight) =>
        insight.insight_id === insightData.insight_id ? { ...insight, ...insightData } : insight
      );
      let updatedData = { ...data, insights: updatedInsights };

      // Propagate downstream if weight changed
      if (weightChanged) {
        const { data: propagated, impactedCount } = propagateImpact(updatedData, 'insight', insightData.insight_id, 'edited');
        updatedData = propagated;
        onInfo(`Weight changed. ${impactedCount} downstream item(s) marked for review.`);
      }

      setData(updatedData);

      // Mark as needs_review if links changed
      if (linksChanged) {
        setData(prev => prev ? ({
          ...prev,
          insights: prev.insights.map(i =>
            i.insight_id === insightData.insight_id ? { ...i, status: 'needs_review' as const } : i
          ),
        }) : prev);
        if (!weightChanged) onInfo('Sources changed — insight marked for review.');
      }
    }
    setIsInsightDialogVisible(false);
  };

  // MergeDialog callbacks use the hook

  // Confirm valid: clear actionable status
  const handleClearStatus = (insightId: string) => {
    setData(prev => prev ? clearStatus(prev, 'insight', insightId) : prev);
  };

  // Bulk review
  const handleSelectReviewable = () => {
    const reviewable = data.insights.filter(i => isActionableStatus(i.status));
    selectAll(reviewable.map(i => i.insight_id));
    if (reviewable.length > 0) {
      const el = document.getElementById(`insight-${reviewable[0].insight_id}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleBulkReview = () => {
    const reviewable = data.insights.filter(
      i => selectedInsightIds.has(i.insight_id) && isActionableStatus(i.status)
    );
    const items: ReviewItem[] = reviewable
      .map(insight => {
        const parentFact = data.facts.find(f => insight.related_facts.includes(f.fact_id));
        if (!parentFact) return null;
        const oldText = parentFact.versions?.length
          ? parentFact.versions[parentFact.versions.length - 1].text
          : '';
        return {
          id: insight.insight_id,
          entityType: 'insight',
          currentText: insight.text,
          upstreamOldText: oldText,
          upstreamNewText: parentFact.text || '',
          upstreamEntityType: 'fact',
          goal: data.goal || '',
        };
      })
      .filter((x): x is ReviewItem => x !== null);
    if (items.length === 0) { onError('No reviewable insights with valid upstream found.'); return; }
    setBulkReviewItems(items);
  };

  const handleBulkReviewAccept = async (id: string, _entityType: string, newText: string) => {
    const latest = dataRef.current;
    const insight = latest.insights.find(i => i.insight_id === id);
    if (!insight) return;
    const versioned = createNewVersion(insight, newText) as InsightType;
    let updatedData = { ...latest, insights: latest.insights.map(i => i.insight_id === id ? versioned : i) };
    updatedData = clearStatus(updatedData, 'insight', id);
    const children = getDirectChildren('insight', id, updatedData);
    const { ids: impactedIds, usedFallback } = await checkImpact(insight.text, newText, children, backendAvailable);
    const { data: propagated, impactedCount } = propagateImpact(updatedData, 'insight', id, 'edited', impactedIds);
    dataRef.current = propagated;
    setData(propagated);
    const fallbackHint = usedFallback ? ' (AI unavailable — all children marked)' : '';
    onInfo(`Insight updated to v${versioned.version}. ${impactedCount} downstream item(s) marked.${fallbackHint}`);
  };

  const handleBulkReviewReject = (id: string) => {
    setData(prev => prev ? clearStatus(prev, 'insight', id) : prev);
  };

  const deleteInsight = (insightId: string) => {
    const updatedInsights = data.insights.filter((insight) => insight.insight_id !== insightId);
    setData((prevState) => prevState ? ({
      ...prevState,
      insights: updatedInsights
    }) : prevState);
  };

  // Generate recommendations from selected insights
  const handleExtractRecommendations = async () => {
    const selected = data.insights.filter(i => selectedInsightIds.has(i.insight_id));
    if (selected.length === 0) return;

    setExtractingRecommendations(true);
    onWaiting('Generating recommendations...');
    try {
      const response = await fetch(`${API_URL}/extract/recommendations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insights: selected.map(i => ({ insight_id: i.insight_id, text: i.text })),
          goal: data.goal,
          ...(data.language ? { language: data.language } : {}),
        }),
      });
      if (!response.ok) {
        const body = await response.json();
        onError(body.error || 'Recommendations extraction failed');
        return;
      }
      const result = await response.json();
      if (result.suggestions.length === 0) {
        onError('No recommendations could be formulated from the selected insights.');
        return;
      }
      onInfo(`Generated ${result.suggestions.length} suggestion(s).`);
      setRecommendationSuggestionData({ suggestions: result.suggestions, insightIds: result.insight_ids });
    } catch (err: any) {
      onError(err.message || 'Recommendations extraction request failed');
    } finally {
      setExtractingRecommendations(false);
    }
  };

  // Accept a recommendation suggestion
  const addRecommendationToData = useCallback((text: string, relatedInsights: string[]) => {
    const newRecommendation: RecommendationType = {
      recommendation_id: Math.random().toString(16).slice(2),
      text,
      related_insights: relatedInsights,
    };
    setData((prevState) => prevState ? ({
      ...prevState,
      recommendations: [...prevState.recommendations, newRecommendation],
    }) : prevState);
  }, [setData]);

  const handleAcceptRecommendation = async (suggestion: { text: string; related_insight_ids?: string[]; weight?: number }) => {
    const relatedInsights = suggestion.related_insight_ids && suggestion.related_insight_ids.length > 0
      ? suggestion.related_insight_ids
      : Array.from(selectedInsightIds);
    const newRecommendation: RecommendationType = {
      recommendation_id: Math.random().toString(16).slice(2),
      text: suggestion.text,
      related_insights: relatedInsights,
      weight: suggestion.weight,
    };
    recommendationDedupQueue.trackStart();
    const duplicates = await findDuplicates(
      suggestion.text,
      data.recommendations.map(r => ({ id: r.recommendation_id, text: r.text })),
      backendAvailable,
    );
    if (duplicates.length > 0) {
      recommendationDedupQueue.enqueue(newRecommendation, duplicates[0]);
      return;
    }
    recommendationDedupQueue.trackComplete();
    addRecommendationToData(suggestion.text, relatedInsights);
  };

  const addRecommendationFromMerge = useCallback((rec: RecommendationType) => {
    setData(prev => prev ? { ...prev, recommendations: [...prev.recommendations, rec] } : prev);
  }, [setData]);

  const handleCloseSuggestions = useCallback(() => {
    setRecommendationSuggestionData(null);
    if (recommendationDedupQueue.queue.length > 0 || recommendationDedupQueue.inflight > 0) {
      recommendationDedupQueue.openReview();
    }
  }, [recommendationDedupQueue]);

  // Auto-close review panel if all inflight resolved and queue is empty
  useEffect(() => {
    if (recommendationDedupQueue.reviewVisible && recommendationDedupQueue.inflight === 0 && recommendationDedupQueue.queue.length === 0) {
      recommendationDedupQueue.reset();
    }
  }, [recommendationDedupQueue.reviewVisible, recommendationDedupQueue.inflight, recommendationDedupQueue.queue.length, recommendationDedupQueue]);

  const handleAddRecommendationFromSelection = () => {
    setIsRecommendationModalVisible(true);
  };

  const saveRecommendationFromSelection = (recommendationData: RecommendationType) => {
    addRecommendationToData(recommendationData.text, recommendationData.related_insights);
    setIsRecommendationModalVisible(false);
  };

  return (
    <div className="column insights">
      <div className="column-sticky-top">
        <div className="column-header">
          <h2>💡Insights</h2>
          {reviewableCount > 0 && <button className="review-select-btn" onClick={handleSelectReviewable}>{reviewableCount} to review</button>}
          {data.insights.length > 0 && selectedInsightIds.size < data.insights.length && (
            <button className="select-all-button" onClick={() => selectAll(data.insights.map(i => i.insight_id))} title="Select all insights">
              <FontAwesomeIcon icon={faCheckDouble} /> Select All
            </button>
          )}
          <button className="header-add-button" onClick={openAddModal} title="Add Insight"><FontAwesomeIcon icon={faAdd} /></button>
        </div>
        <div className={`toolbar-wrapper${selectedInsightIds.size > 0 ? ' toolbar-wrapper-open' : ''}`}>
          <div className="selection-toolbar">
            <span>{selectedInsightIds.size} insight(s) selected</span>
            {data.insights.some(i => selectedInsightIds.has(i.insight_id) && isActionableStatus(i.status)) && backendAvailable && (
              <button className="toolbar-review-btn" onClick={handleBulkReview} title="AI review proposals for flagged items">
                <FontAwesomeIcon icon={faClipboardCheck} />
                {' '}Review
              </button>
            )}
            <button onClick={handleExtractRecommendations} disabled={extractingRecommendations || !backendAvailable} title={!backendAvailable ? 'Backend unavailable' : ''}>
              <FontAwesomeIcon icon={extractingRecommendations ? faSpinner : faWandMagicSparkles} spin={extractingRecommendations} />
              {' '}Generate Recommendations
            </button>
            <button onClick={handleAddRecommendationFromSelection}>
              <FontAwesomeIcon icon={faClipboardList} />
              {' '}Add Recommendation
            </button>
            <button onClick={() => setConfirmBulkDelete(true)}>
              <FontAwesomeIcon icon={faTrashCan} />
              {' '}Delete
            </button>
            <button onClick={clearSelection}>
              <FontAwesomeIcon icon={faXmark} />
              {' '}Clear
            </button>
          </div>
        </div>
      </div>
      {data.insights.length === 0 && (
        <p className="empty-state-hint">Select facts and generate insights, or add them manually.</p>
      )}
      {data.insights.map((insight, index) => (
        <div
          key={insight.insight_id}
          onClick={() => toggleInsightSelection(insight.insight_id)}
          className={selectedInsightIds.has(insight.insight_id) ? 'item-selectable selected' : 'item-selectable'}
        >
          <ItemWrapper
            id={"insight-" + insight.insight_id}
            index={index}
            item={insight}
            setItemRef={setInsightRef}
            handleMouseEnter={() => handleMouseEnter("insight", insight.insight_id, data)}
            handleMouseLeave={() => handleMouseLeave("insight", insight.insight_id, data)}
            openEditModal={openEditModal}
            onViewTraceability={() => onViewTraceability("insight", insight.insight_id)}
            onClearStatus={() => handleClearStatus(insight.insight_id)}
            onDelete={() => requestConfirm?.('Delete this insight?', () => deleteInsight(insight.insight_id))}
          >
            <InsightItem insight={insight} />
          </ItemWrapper>
        </div>
      ))}
      <InsightModal
        mode={modalMode}
        isDialogVisible={isInsightDialogVisible}
        closeDialog={() => setIsInsightDialogVisible(false)}
        saveInsight={saveInsight}
        deleteInsight={deleteInsight}
        insightData={editingInsight as InsightType}
        facts={data.facts}
        backendAvailable={backendAvailable}
        goal={data.goal}
      />
      <RecommendationModal
        mode="add"
        isDialogVisible={isRecommendationModalVisible}
        closeDialog={() => setIsRecommendationModalVisible(false)}
        saveRecommendation={saveRecommendationFromSelection}
        deleteRecommendation={() => {}}
        recommendationData={{ recommendation_id: '', text: '', related_insights: Array.from(selectedInsightIds) } as RecommendationType}
        insights={data.insights}
        backendAvailable={backendAvailable}
        goal={data.goal}
      />
      <Modal isVisible={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)} maxWidth="400px">
        <p style={{ margin: '0 0 1em' }}>Delete {selectedInsightIds.size} selected insight(s)?</p>
        <div className="modal-actions">
          <div className="modal-action-group-left">
            <button className="modal-action-save" onClick={() => { setData(prev => prev ? { ...prev, insights: prev.insights.filter(n => !selectedInsightIds.has(n.insight_id)) } : prev); clearSelection(); setConfirmBulkDelete(false); }}>Confirm</button>
          </div>
          <div className="modal-action-group-right">
            <button className="modal-action-close" onClick={() => setConfirmBulkDelete(false)}>Cancel</button>
          </div>
        </div>
      </Modal>
      {recommendationSuggestionData && (
        <SuggestionsPanel
          suggestions={recommendationSuggestionData.suggestions}
          inputId="recommendations"
          title="Suggested Recommendations"
          onAccept={(suggestion) => handleAcceptRecommendation(suggestion)}
          onClose={handleCloseSuggestions}
        />
      )}
      {mergeDialog.match && (
        <MergeDialog
          isVisible={mergeDialog.visible}
          newText={mergeDialog.pendingItem?.text || ''}
          existingItem={mergeDialog.match}
          onMerge={() => mergeDialog.handleMerge((pending, match) => {
            setData(prev => prev ? {
              ...prev,
              insights: prev.insights.map(i => i.insight_id === match.id
                ? { ...i, related_facts: Array.from(new Set([...i.related_facts, ...pending.related_facts])) }
                : i),
            } : prev);
          })}
          onKeepAsVariant={() => mergeDialog.handleKeepAsVariant(addInsightToData)}
          onForceAdd={() => mergeDialog.handleForceAdd(addInsightToData)}
          onClose={mergeDialog.reset}
        />
      )}
      {recommendationDedupQueue.reviewVisible && recommendationDedupQueue.queue.length > 0 && (
        <BatchDedupReviewPanel<RecommendationType>
          entries={recommendationDedupQueue.queue}
          inflight={recommendationDedupQueue.inflight}
          getText={(item) => item.text}
          onResolve={recommendationDedupQueue.resolveEntry}
          onResolveAll={recommendationDedupQueue.resolveAll}
          onApply={() => recommendationDedupQueue.applyAll(
            (pending, match) => {
              setData(prev => prev ? {
                ...prev,
                recommendations: prev.recommendations.map(r => r.recommendation_id === match.id
                  ? { ...r, related_insights: Array.from(new Set([...r.related_insights, ...pending.related_insights])) }
                  : r),
              } : prev);
            },
            addRecommendationFromMerge,
          )}
          onClose={recommendationDedupQueue.reset}
        />
      )}
      {bulkReviewItems && (
        <BulkReviewPanel
          items={bulkReviewItems}
          onAccept={handleBulkReviewAccept}
          onReject={handleBulkReviewReject}
          onClose={() => setBulkReviewItems(null)}
        />
      )}
    </div>
  );
};

export default InsightList;
